/**
 * LinkAgentToTenantDialog - Modal para vincular um agente a outro tenant
 * 
 * Permite que administradores vinculem agentes de um tenant a outros tenants
 * que o admin também gerencia.
 * 
 * Funcionalidades:
 * - Lista apenas tenants onde o usuário logado é administrador
 * - Seleção de função no novo tenant (Agente/Supervisor/Administrador)
 * - Perfil de permissão para supervisores
 * - Validação de vínculo duplicado
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, AlertCircle, Link2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

// Tipo de função disponível
// Tipo de função: 'admin' = tenant_admin (NÃO é proprietário/owner)
type AgentRole = 'agent' | 'supervisor' | 'admin';

// Interface do agente
interface Agent {
  id: string;
  user_id: string;
  email?: string;
  full_name?: string;
}

// Interface do tenant disponível para vínculo
interface AvailableTenant {
  id: string;
  name: string;
  slug: string;
}

// Interface do perfil de permissão
interface PermissionProfile {
  id: string;
  name: string;
}

interface LinkAgentToTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agent: Agent | null;
  currentTenantId: string;
  currentUserId: string;
  onSuccess: () => void;
}

const LinkAgentToTenantDialog = ({
  open,
  onOpenChange,
  agent,
  currentTenantId,
  currentUserId,
  onSuccess,
}: LinkAgentToTenantDialogProps) => {
  // Estados do formulário
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [role, setRole] = useState<AgentRole>('agent');
  
  const [permissionProfileId, setPermissionProfileId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Estados dos dados
  const [availableTenants, setAvailableTenants] = useState<AvailableTenant[]>([]);
  const [profiles, setProfiles] = useState<PermissionProfile[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  // Busca tenants administrados quando abre o dialog
  useEffect(() => {
    if (open && currentUserId) {
      fetchAdminTenants();
      resetForm();
    }
  }, [open, currentUserId, currentTenantId, agent?.user_id]);

  // Busca perfis de permissão do tenant selecionado
  useEffect(() => {
    if (selectedTenantId && role === 'supervisor') {
      fetchProfiles(selectedTenantId);
    } else {
      setProfiles([]);
      setPermissionProfileId(null);
    }
  }, [selectedTenantId, role]);

  /**
   * Reseta o formulário para valores iniciais
   */
  const resetForm = () => {
    setSelectedTenantId('');
    setRole('agent');
    setPermissionProfileId(null);
    setProfiles([]);
  };

  /**
   * Busca todos os tenants onde o usuário logado é administrador
   * Exclui o tenant atual da lista
   */
  const fetchAdminTenants = async () => {
    setLoadingTenants(true);
    try {
      // Busca tenant_members onde o usuário é owner ou tem role de admin
      const { data: memberships, error: membershipsError } = await supabase
        .from('tenant_members')
        .select(`
          tenant_id,
          is_owner,
          tenants (id, name, slug, status)
        `)
        .eq('user_id', currentUserId)
        .eq('is_active', true);

      if (membershipsError) throw membershipsError;

      // Busca roles do usuário para verificar quais são admin
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('tenant_id, role')
        .eq('user_id', currentUserId)
        .in('role', ['tenant_admin', 'root_admin']);

      if (rolesError) throw rolesError;

      // Busca tenants onde o agente JÁ é membro ativo (para excluir da lista)
      const { data: agentMemberships } = await supabase
        .from('tenant_members')
        .select('tenant_id')
        .eq('user_id', agent.user_id)
        .eq('is_active', true);

      const agentTenantIds = new Set((agentMemberships || []).map(m => m.tenant_id));

      // Cria um set de tenant_ids onde o usuário é admin
      const adminTenantIds = new Set<string>();
      
      // Adiciona tenants onde é owner
      memberships?.forEach((m) => {
        if (m.is_owner) {
          adminTenantIds.add(m.tenant_id);
        }
      });

      // Adiciona tenants onde tem role de admin
      userRoles?.forEach((r) => {
        adminTenantIds.add(r.tenant_id);
      });

      // Filtra tenants: apenas onde é admin, exclui tenant atual e tenants onde o agente já é membro
      const filteredTenants: AvailableTenant[] = [];
      memberships?.forEach((m) => {
        const tenant = m.tenants as unknown as { id: string; name: string; slug: string; status: string };
        if (
          tenant &&
          adminTenantIds.has(m.tenant_id) &&
          m.tenant_id !== currentTenantId &&
          !agentTenantIds.has(m.tenant_id) &&
          tenant.status === 'active'
        ) {
          filteredTenants.push({
            id: tenant.id,
            name: tenant.name,
            slug: tenant.slug,
          });
        }
      });

      setAvailableTenants(filteredTenants);
      logger.debug('[LinkAgentToTenantDialog] Tenants administrados encontrados:', String(filteredTenants.length));
    } catch (error) {
      logger.error('[LinkAgentToTenantDialog] Erro ao buscar tenants:', error);
      toast.error('Erro ao carregar tenants disponíveis');
    } finally {
      setLoadingTenants(false);
    }
  };

  /**
   * Busca perfis de permissão ativos do tenant selecionado
   */
  const fetchProfiles = async (tenantId: string) => {
    setLoadingProfiles(true);
    try {
      // Server-side: substitui SELECT direto em permission_profiles por RPC
      const { data, error } = await supabase.rpc('get_permission_profiles_list', {
        p_tenant_hint: tenantId,
      });

      if (error) throw error;
      const activeOnly = ((data || []) as any[])
        .filter(p => p.is_active)
        .map(p => ({ id: p.id, name: p.name }));
      setProfiles(activeOnly);
    } catch (error) {
      logger.error('[LinkAgentToTenantDialog] Erro ao buscar perfis:', error);
    } finally {
      setLoadingProfiles(false);
    }
  };

  /**
   * Vincula o agente ao tenant selecionado via RPC link_agent_to_tenant
   * A RPC já valida: duplicatas, limites de agentes, roles e reativação de inativos
   */
  const handleLink = async () => {
    if (!agent?.user_id || !selectedTenantId) {
      toast.error('Selecione um tenant destino');
      return;
    }

    // Validação: supervisor precisa de perfil
    if (role === 'supervisor' && !permissionProfileId) {
      toast.error('Selecione um perfil de permissão para o supervisor');
      return;
    }

    setSaving(true);
    try {
      // Mapeia role do select para o formato esperado pela RPC
      const dbRole = role === 'admin' 
        ? 'tenant_admin' 
        : role === 'supervisor' 
          ? 'tenant_supervisor' 
          : 'agent';

      // Chamada à RPC centralizada (SECURITY DEFINER — bypass RLS)
      // Valida limites de agentes, duplicatas e integridade do owner automaticamente
      const { data, error } = await supabase.rpc('link_agent_to_tenant', {
        p_agent_id: agent.user_id,
        p_target_tenant_id: selectedTenantId,
        p_role: dbRole,
        p_permission_profile_id: role === 'supervisor' ? permissionProfileId : null,
      });

      if (error) throw error;

      const result = data as { success: boolean; message: string; reactivated: boolean } | null;
      const targetTenant = availableTenants.find(t => t.id === selectedTenantId);
      
      if (result?.reactivated) {
        toast.success(`Agente reativado em "${targetTenant?.name}" com sucesso!`);
      } else {
        toast.success(`Agente vinculado a "${targetTenant?.name}" com sucesso!`);
      }

      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      logger.error('[LinkAgentToTenantDialog] Erro ao vincular agente:', error);
      // Mensagens amigáveis baseadas nos erros da RPC
      const msg = error?.message || '';
      if (msg.includes('Limite de agentes')) {
        toast.error('Limite de agentes atingido neste tenant. Faça upgrade do plano.');
      } else if (msg.includes('já está vinculado')) {
        toast.error('Este agente já está vinculado a este tenant');
      } else if (msg.includes('permissão')) {
        toast.error('Você não tem permissão para vincular agentes neste tenant');
      } else {
        toast.error('Erro ao vincular agente ao tenant');
      }
    } finally {
      setSaving(false);
    }
  };

  // Verifica se há tenants disponíveis
  const hasAvailableTenants = availableTenants.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-primary" />
            Vincular a Outro Tenant
          </DialogTitle>
          <DialogDescription>
            Vincular "{agent?.full_name || agent?.email}" a outra empresa que você administra.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {loadingTenants ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : !hasAvailableTenants ? (
            <div className="flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Você não administra outros tenants além do atual. 
                Só é possível vincular agentes a tenants que você gerencia.
              </p>
            </div>
          ) : (
            <>
              {/* Seletor de Tenant Destino */}
              <div className="space-y-2">
                <Label htmlFor="target-tenant">Tenant Destino</Label>
                <Select 
                  value={selectedTenantId} 
                  onValueChange={setSelectedTenantId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tenant">
                      {selectedTenantId && (
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          {availableTenants.find(t => t.id === selectedTenantId)?.name}
                        </div>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {availableTenants.map((tenant) => (
                      <SelectItem key={tenant.id} value={tenant.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          {tenant.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Selecione a empresa onde deseja vincular este agente.
                </p>
              </div>

              {/* Seletor de Função */}
              <div className="space-y-2">
                <Label htmlFor="role">Função no Novo Tenant</Label>
                <Select 
                  value={role} 
                  onValueChange={(value: AgentRole) => {
                    setRole(value);
                    if (value !== 'supervisor') {
                      setPermissionProfileId(null);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a função" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agent">Agente</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {role === 'admin' && 'Administradores têm acesso total ao tenant.'}
                  {role === 'supervisor' && 'Supervisores têm permissões configuráveis.'}
                  {role === 'agent' && 'Agentes têm acesso apenas ao chat.'}
                </p>
              </div>

              {/* Seletor de Perfil de Permissão (apenas para supervisores) */}
              {role === 'supervisor' && selectedTenantId && (
                <div className="space-y-2">
                  <Label htmlFor="profile">Perfil de Permissão *</Label>
                  {loadingProfiles ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Carregando perfis...
                    </div>
                  ) : profiles.length === 0 ? (
                    <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-md">
                      <AlertCircle className="h-4 w-4 text-amber-500" />
                      <p className="text-sm text-amber-600 dark:text-amber-400">
                        Este tenant não possui perfis de permissão cadastrados.
                      </p>
                    </div>
                  ) : (
                    <Select 
                      value={permissionProfileId || ''} 
                      onValueChange={setPermissionProfileId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o perfil" />
                      </SelectTrigger>
                      <SelectContent>
                        {profiles.map((profile) => (
                          <SelectItem key={profile.id} value={profile.id}>
                            {profile.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* Toggle de proprietário removido — is_owner é exclusivo do criador (trigger fn_protect_owner_integrity) */}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleLink} 
            disabled={
              saving || 
              !hasAvailableTenants || 
              !selectedTenantId || 
              (role === 'supervisor' && !permissionProfileId && profiles.length > 0)
            }
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Link2 className="h-4 w-4 mr-2" />
            Vincular
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkAgentToTenantDialog;
